<script setup>
import { computed, onMounted } from "vue";
import { useMediaQuery } from "@vueuse/core";
import { useGlobalStore } from "@stores/global";
import { useGlobeStore } from "@stores/globe";
import { useExpediaStore } from "@stores/expedia";
import { useActivityStore } from "@stores/activity";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import IconPin from "@components/icons/Pin.vue";
import IconArrowUpRight from "@components/icons/ArrowUpRight.vue";
import PanelTabs from "@components/explore/PanelTabs.vue";
import PanelFooter from "@components/explore/PanelFooter.vue";

const globalStore = useGlobalStore();
const globeStore = useGlobeStore();
const expediaStore = useExpediaStore();
const activityStore = useActivityStore();
const isMobile = useMediaQuery("(max-width: 1024px)");

const carousel = computed(() => activityStore.featuredCarousel);

const featured = computed(() =>
    (carousel.value?.activities ?? []).map((activity) => ({
        name: activity.name,
        location: [activity.city?.name, activity.city?.country?.name]
            .filter(Boolean)
            .join(", "),
        url: activity.guide_url,
    })),
);

onMounted(() => activityStore.loadFeaturedCarousel());
</script>

<template>
    <Transition name="panel" appear>
        <div
            v-if="
                carousel &&
                globalStore.panelActive('featuredActivities') &&
                !globeStore.selectedCountry &&
                globalStore.activePage === 'explore'
            "
            class="featuredactivities bg-marine-800 border-marine-500 flex flex-col gap-6 overflow-hidden overflow-y-auto border-b p-6 max-lg:border-0"
            :class="{
                'max-lg:hidden':
                    globalStore.mobilePanel !== 'featuredActivities',
            }"
        >
            <PanelTabs />

            <div class="flex flex-col gap-6 max-lg:h-[180px]">
                <p class="h6 leading-[0.9] font-bold uppercase">
                    {{ carousel.title }}
                </p>

                <div v-if="isMobile" class="min-w-0">
                    <Swiper
                        :modules="[Pagination]"
                        :slides-per-view="1.1"
                        :space-between="8"
                        :pagination="{ clickable: true }"
                        class="featured-swiper overflow-visible!"
                    >
                        <SwiperSlide v-for="(item, i) in featured" :key="i">
                            <a
                                :href="expediaStore.href(item.url)"
                                target="_blank"
                                rel="noopener"
                                class="bg-marine-600 flex min-h-14 items-center gap-3 p-3"
                            >
                                <div class="flex min-w-0 flex-1 flex-col gap-2">
                                    <p
                                        class="body-md leading-[0.9] font-bold uppercase"
                                    >
                                        {{ item.name }}
                                    </p>
                                    <div class="flex items-center gap-1">
                                        <IconPin
                                            class="size-3 shrink-0 text-yellow-400"
                                        />
                                        <p
                                            class="body-xs faded-text leading-[0.9] font-medium uppercase"
                                        >
                                            {{ item.location }}
                                        </p>
                                    </div>
                                </div>
                                <div
                                    class="text-marine-800 flex size-6 shrink-0 items-center justify-center rounded-sm bg-yellow-400"
                                >
                                    <IconArrowUpRight class="size-3.5" />
                                </div>
                            </a>
                        </SwiperSlide>
                    </Swiper>
                </div>

                <ul v-else class="flex w-full flex-col gap-1">
                    <li v-for="(item, i) in featured" :key="i">
                        <a
                            :href="expediaStore.href(item.url)"
                            target="_blank"
                            rel="noopener"
                            class="bg-marine-600 flex min-h-14 items-center gap-3 p-3"
                        >
                            <div class="flex min-w-0 flex-1 flex-col gap-2">
                                <p
                                    class="body-md leading-[0.9] font-bold uppercase"
                                >
                                    {{ item.name }}
                                </p>
                                <div class="flex items-center gap-1">
                                    <IconPin
                                        class="size-3 shrink-0 text-yellow-400"
                                    />
                                    <p
                                        class="body-xs faded-text leading-[0.9] font-medium uppercase"
                                    >
                                        {{ item.location }}
                                    </p>
                                </div>
                            </div>
                            <div
                                class="text-marine-800 flex size-6 shrink-0 items-center justify-center rounded-sm bg-yellow-400"
                            >
                                <IconArrowUpRight class="size-3.5" />
                            </div>
                        </a>
                    </li>
                </ul>
            </div>

            <PanelFooter class="lg:hidden" />
        </div>
    </Transition>
</template>

<style scoped>
.featuredactivities {
    grid-column: -7 / -1;
    grid-row: 2 / span 6;
    z-index: 2;
}

@media (max-width: 1024px) {
    .featuredactivities {
        grid-column: 1 / -1;
        grid-row: -2 / -1;
        align-self: end;
    }
}

.featured-swiper {
    width: 100%;
    margin-inline: 0;
    --swiper-pagination-color: var(--color-yellow-400);
    --swiper-pagination-bullet-inactive-color: #fff;
    --swiper-pagination-bullet-inactive-opacity: 0.2;
    --swiper-pagination-bullet-size: 6px;
    --swiper-pagination-bullet-horizontal-gap: 2px;
    --swiper-pagination-bottom: 0;
    padding-bottom: 1.5rem;
}

.featured-swiper :deep(.swiper-pagination-bullet) {
    border-radius: 0;
}
</style>
